import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, ArrowRight, CheckCircle, Clock, AlertTriangle, Zap, Shield, Wrench
} from 'lucide-react';
import { companyInfo } from '../../data/mock';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import SEO from '../seo/SEO';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';

const VitreCasseePage = () => {
  const pageTitle = "Remplacement Vitre Cassée Villiers-sur-Marne (94)";
  const pageDescription = "Remplacement vitre cassée à Villiers-sur-Marne ☎ 07 81 55 90 50. Intervention rapide vitrier pour vitre cassée. Simple vitrage, double vitrage, verre sécurité. Devis gratuit.";
  const pageKeywords = "remplacement vitre cassée Villiers-sur-Marne, vitre cassée, changement vitre, vitrier vitre cassée, réparation vitre, remplacement vitrage 94350";

  const typesVitrage = [
    {
      title: "Simple vitrage",
      desc: "Remplacement vitre simple pour fenêtres anciennes",
      price: "À partir de 45€"
    },
    {
      title: "Double vitrage 4/16/4",
      desc: "Vitrage isolant standard avec lame d'air",
      price: "À partir de 85€"
    },
    {
      title: "Double vitrage ITR",
      desc: "Isolation Thermique Renforcée",
      price: "À partir de 110€"
    },
    {
      title: "Verre feuilleté",
      desc: "Vitrage de sécurité anti-effraction",
      price: "À partir de 95€"
    },
    {
      title: "Verre trempé",
      desc: "Verre sécurité haute résistance",
      price: "À partir de 120€"
    },
    {
      title: "Triple vitrage",
      desc: "Isolation maximale thermique et phonique",
      price: "À partir de 150€"
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonical="/remplacement-vitre-cassee-villiers-sur-marne"
      />

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/323776/pexels-photo-323776.jpeg?auto=compress&cs=tinysrgb&w=1920)' }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="container mx-auto px-4 relative z-10">
          <Breadcrumb className="mb-6">
            <BreadcrumbList className="text-white/70">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="hover:text-white">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/vitrier-villiers-sur-marne" className="hover:text-white">Vitrier Villiers-sur-Marne</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">Remplacement vitre cassée</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="max-w-4xl">
            <Badge className="badge-emergency mb-6">
              <Wrench className="w-3 h-3 mr-1" /> Intervention rapide
            </Badge>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Remplacement <span className="text-[hsl(24,95%,53%)]">Vitre Cassée</span>
              <span className="block text-3xl mt-2">Villiers-sur-Marne</span>
            </h1>
            
            <p className="text-xl text-gray-200 mb-8">
              Votre vitre est cassée ? Notre vitrier intervient rapidement à Villiers-sur-Marne 
              pour le remplacement de tout type de vitrage : simple, double, triple vitrage.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <a href={`tel:${companyInfo.phoneEmergency}`}>
                <Button size="lg" className="emergency-btn rounded-full text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  {companyInfo.phoneEmergency}
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full text-lg px-8 py-6">
                  Devis gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                <span>Tous types de vitrage</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                <span>Intervention -1h</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                <span>Garantie travaux</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types de vitrage */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-4 text-center">
            Types de vitrage pour remplacement vitre cassée
          </h2>
          <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
            Notre vitrier à Villiers-sur-Marne remplace tous types de vitrages. 
            Tarifs indicatifs, devis gratuit sur place.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {typesVitrage.map((item, i) => (
              <Card key={i} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-bold text-[hsl(215,25%,15%)] mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm mb-3">{item.desc}</p>
                  <p className="text-[hsl(207,78%,42%)] font-semibold">{item.price}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Processus */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-10 text-center">
            Comment se déroule le remplacement de votre vitre cassée ?
          </h2>
          
          <div className="grid md:grid-cols-5 gap-4">
            {[
              { step: "1", title: "Appel", desc: "Contactez-nous" },
              { step: "2", title: "Diagnostic", desc: "Analyse du vitrage" },
              { step: "3", title: "Devis", desc: "Estimation gratuite" },
              { step: "4", title: "Remplacement", desc: "Pose du nouveau vitrage" },
              { step: "5", title: "Nettoyage", desc: "Finitions et propreté" }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[hsl(207,78%,42%)] text-white text-xl font-bold flex items-center justify-center mx-auto mb-3">
                  {item.step}
                </div>
                <h3 className="font-bold text-[hsl(215,25%,15%)] text-sm mb-1">{item.title}</h3>
                <p className="text-muted-foreground text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contenu SEO */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">
              Remplacement de vitre cassée à Villiers-sur-Marne
            </h2>
            
            <div className="prose prose-lg text-muted-foreground space-y-4">
              <p>
                Une <strong>vitre cassée</strong> peut survenir à tout moment : accident domestique, 
                tentative d'effraction, choc thermique, ou simplement l'usure du temps. 
                <strong>NGL SERVICES</strong>, votre <strong>vitrier à Villiers-sur-Marne</strong>, 
                intervient rapidement pour le <strong>remplacement de votre vitre cassée</strong>.
              </p>
              
              <p>
                Nos artisans vitriers sont équipés pour remplacer tous types de vitrage : 
                <strong>simple vitrage</strong>, <strong>double vitrage</strong>, <strong>triple vitrage</strong>, 
                <strong>verre feuilleté</strong> de sécurité, <strong>verre trempé</strong>. 
                Nous intervenons sur fenêtres, baies vitrées, portes vitrées, vérandas et vitrines de commerce.
              </p>

              <h3 className="text-xl font-bold text-[hsl(215,25%,15%)] mt-8 mb-4">
                Pourquoi faire appel à un vitrier professionnel ?
              </h3>
              
              <ul className="space-y-2">
                <li><strong>Sécurité</strong> : Manipulation sécurisée du verre cassé</li>
                <li><strong>Précision</strong> : Prise de mesures exactes pour un vitrage sur-mesure</li>
                <li><strong>Qualité</strong> : Pose professionnelle garantissant l'étanchéité</li>
                <li><strong>Garantie</strong> : Travaux garantis et matériaux de qualité</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Liens internes */}
      <section className="py-12 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-6">Services associés :</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/vitrier-villiers-sur-marne" className="px-4 py-2 bg-white text-[hsl(207,78%,42%)] rounded-full text-sm shadow hover:shadow-md">
              Vitrier Villiers-sur-Marne
            </Link>
            <Link to="/vitrier-urgence-villiers-sur-marne" className="px-4 py-2 bg-white text-[hsl(207,78%,42%)] rounded-full text-sm shadow hover:shadow-md">
              Vitrier urgence 24h/24
            </Link>
            <Link to="/bris-de-glace-villiers-sur-marne" className="px-4 py-2 bg-white text-[hsl(207,78%,42%)] rounded-full text-sm shadow hover:shadow-md">
              Bris de glace
            </Link>
            <Link to="/services/vitrerie" className="px-4 py-2 bg-white text-[hsl(207,78%,42%)] rounded-full text-sm shadow hover:shadow-md">
              Services vitrerie
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[hsl(215,25%,15%)]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Vitre cassée à Villiers-sur-Marne ?
          </h2>
          <p className="text-gray-300 mb-8">
            Contactez NGL SERVICES pour un remplacement rapide et professionnel.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="emergency-btn rounded-full text-lg">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phoneEmergency}
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full text-lg">
                Devis gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VitreCasseePage;
